import { ProgressToast } from '@/components/ui/ProgressToast';
import { createElement, useCallback, useEffect } from 'react';
import toast from 'react-hot-toast';
import { SimulatedProgressBatchDrive, useSimulatedProgress } from './useSimulatedProgress';

interface UseProgressToastOptions {
  label?: string;
  description?: string;
  toastId?: string;
  batchDrive?: SimulatedProgressBatchDrive;
  instant?: boolean;
  onCancel?: () => void;
  cancelLabel?: string;
}

export function useProgressToast({
  label,
  description,
  toastId = 'simulated-progress',
  batchDrive,
  instant = false,
  onCancel,
  cancelLabel,
}: UseProgressToastOptions = {}) {
  const {
    progress,
    start,
    startControlled,
    setProgressValue,
    finish: finishSimulated,
    cancel: cancelSimulated,
  } = useSimulatedProgress({ batchDrive });

  const finish = useCallback(
    (onDone?: () => void) => {
      finishSimulated(() => {
        toast.dismiss(toastId);
        onDone?.();
      });
    },
    [finishSimulated, toastId],
  );

  const cancel = useCallback(() => {
    cancelSimulated();
    toast.dismiss(toastId);
  }, [cancelSimulated, toastId]);

  const run = useCallback(
    async <T>(fn: () => Promise<T>, onDone?: () => void): Promise<T> => {
      start();
      try {
        const result = await fn();
        finish(onDone);
        return result;
      } catch (error) {
        cancel();
        throw error;
      }
    },
    [start, finish, cancel],
  );

  useEffect(() => {
    if (progress === null) {
      toast.dismiss(toastId);
      return;
    }

    toast.custom(
      (t) =>
        createElement(ProgressToast, {
          value: progress,
          visible: t.visible,
          label,
          description,
          instant,
          toastId,
          onCancel,
          cancelLabel,
        }),
      { id: toastId, duration: Infinity },
    );
  }, [progress, label, description, toastId, instant, onCancel, cancelLabel]);

  return { run, start, startControlled, setProgressValue, finish, cancel };
}
